// Projects data loader - fetches from CDN or falls back to local data
// This allows projects data to be updated without redeploying the app

import { projectsData as localProjectsData } from './projectsData';
import { CDN_URLS } from '../config/cdnConfig';

// CDN URL for projects data (can be overridden via environment variable)
// Use backend proxy to avoid CORS issues, fallback to CDN or direct GCS URL
const getProjectsDataUrl = () => {
  if (process.env.REACT_APP_PROJECTS_DATA_URL) {
    return process.env.REACT_APP_PROJECTS_DATA_URL;
  }
  // Try backend proxy first (no CORS issues)
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.snageasy.in';
  if (apiUrl) {
    return `${apiUrl}/api/projects-data`;
  }
  // Fallback to CDN URL (or direct GCS if CDN not configured)
  return CDN_URLS.PROJECTS_DATA;
};

const PROJECTS_DATA_CDN_URL = getProjectsDataUrl();

// Cache for fetched projects data
let cachedProjectsData = null;
let fetchPromise = null;

/**
 * Fetch projects data from CDN
 * @returns {Promise<Object>} Projects data object
 */
const fetchProjectsDataFromCDN = async () => {
  try {
    console.log(`[Projects Data] Fetching from CDN: ${PROJECTS_DATA_CDN_URL}`);
    const response = await fetch(PROJECTS_DATA_CDN_URL, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'default',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data && typeof data === 'object' && data.projects && Array.isArray(data.projects)) {
      console.log(`[Projects Data] Successfully loaded ${data.projects.length} projects from CDN`);
      return data;
    }
    throw new Error('Invalid data format from CDN');
  } catch (error) {
    console.warn(`[Projects Data] Failed to fetch from CDN: ${error.message}`);
    if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
      console.warn(`[Projects Data] CORS error detected. Check CORS configuration on GCS bucket.`);
      console.warn(`[Projects Data] Run: ./scripts/set-gcs-cors.sh`);
    }
    console.warn(`[Projects Data] Falling back to local projects data`);
    return null;
  }
};

/**
 * Get projects data (from cache, CDN, or local fallback)
 * @returns {Promise<Object>} Projects data object
 */
const getProjectsDataInternal = async () => {
  // Return cached data if available
  if (cachedProjectsData) {
    return cachedProjectsData;
  }

  // If fetch is in progress, wait for it
  if (fetchPromise) {
    return fetchPromise;
  }

  // Fetch from CDN
  fetchPromise = fetchProjectsDataFromCDN().then(data => {
    if (data && typeof data === 'object' && data.projects) {
      cachedProjectsData = data;
      return data;
    }
    // Fallback to local data
    cachedProjectsData = localProjectsData;
    return localProjectsData;
  }).catch(() => {
    // On error, use local fallback
    cachedProjectsData = localProjectsData;
    return localProjectsData;
  });

  return fetchPromise;
};

/**
 * Get all projects data (async - fetches from CDN if available)
 */
export const getProjectsData = async () => {
  return await getProjectsDataInternal();
};

// Initialize: Try to fetch from CDN on module load (non-blocking)
if (typeof window !== 'undefined') {
  getProjectsDataInternal().catch(err => {
    console.error('[Projects Data] Error initializing projects data:', err);
  });
}
